import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Separator } from "../components/ui/separator";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export default function Signup() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setLoading(true);
    try {
      await register(email, password, name);
      toast.success("Welcome to MUConcierge");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create account");
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <Link to="/" className="inline-flex items-center gap-2" data-testid="signup-logo-link">
            <div className="w-9 h-9 bg-[#1C3326] flex items-center justify-center text-[#FAF9F6] font-serif text-lg">M</div>
            <span className="font-serif text-2xl text-[#1A201C]">MUConcierge</span>
          </Link>
        </div>

        <div className="bg-white border border-[#E5E2DC] p-8 sm:p-10">
          <h1 className="font-serif text-3xl text-[#1A201C] mb-2">Create your account</h1>
          <p className="text-sm text-[#5C635A] mb-8">Track your consultations and assembly schedule.</p>

          <Button onClick={googleLogin} variant="outline" className="w-full rounded-sm border-[#E5E2DC] hover:bg-[#F5F3EF] py-6" data-testid="google-signup-btn">
            <svg className="w-4 h-4 mr-3" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
            Continue with Google
          </Button>

          <div className="flex items-center gap-3 my-6">
            <Separator className="flex-1 bg-[#E5E2DC]" />
            <span className="text-xs text-[#5C635A] tag-line">or</span>
            <Separator className="flex-1 bg-[#E5E2DC]" />
          </div>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <Label className="tag-line text-[#1A201C]">Full name</Label>
              <Input required value={name} onChange={(e) => setName(e.target.value)} className="mt-2 rounded-sm border-[#E5E2DC]" data-testid="signup-name" />
            </div>
            <div>
              <Label className="tag-line text-[#1A201C]">Email</Label>
              <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-2 rounded-sm border-[#E5E2DC]" data-testid="signup-email" />
            </div>
            <div>
              <Label className="tag-line text-[#1A201C]">Password</Label>
              <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-2 rounded-sm border-[#E5E2DC]" data-testid="signup-password" />
            </div>
            <Button type="submit" disabled={loading} className="w-full rounded-sm bg-[#1C3326] hover:bg-[#2A4A38] text-[#FAF9F6] py-6" data-testid="signup-submit">
              {loading ? "Creating account..." : "Create account"}
            </Button>
          </form>

          <p className="text-sm text-[#5C635A] text-center mt-6">
            Already have an account?{" "}
            <Link to="/login" className="text-[#1C3326] underline underline-offset-4 hover:text-[#C26D5C]" data-testid="goto-login-link">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
